/* eslint-disable react/jsx-no-constructed-context-values */
import React from "react";
import PropTypes from "prop-types";
import {
  useOverlay,
  DismissButton,
  FocusScope,
  OverlayContainer,
  useModal,
  useOverlayTrigger,
  useOverlayPosition,
  useDialog,
  useButton,
  mergeProps,
} from "react-aria";
import { AnimatePresence, motion } from "framer-motion";
import { useOverlayTriggerState } from "react-stately";

const missingPopoverProvider = "Component must be used within <Popover.Root>";
const PopoverContext = React.createContext({
  get isOpen() {
    throw new Error(missingPopoverProvider);
  },
  get onClose() {
    throw new Error(missingPopoverProvider);
  },
  get triggerProps() {
    throw new Error(missingPopoverProvider);
  },
  get overlayProps() {
    throw new Error(missingPopoverProvider);
  },
  get triggerRef() {
    throw new Error(missingPopoverProvider);
  },
  get overlayRef() {
    throw new Error(missingPopoverProvider);
  },
  get positionStyles() {
    throw new Error(missingPopoverProvider);
  },
  get positionAttributes() {
    throw new Error(missingPopoverProvider);
  },
});
PopoverContext.displayName = "PopoverContext";

const ContentBody = React.forwardRef(({ children, className }, forwardRef) => {
  const {
    isOpen,
    onClose,
    overlayProps: defaultOverlayProps,
    positionProps,
    setPopperElement,
  } = React.useContext(PopoverContext);
  // Hide content outside the modal from screen readers.
  const { modalProps } = useModal();
  // Without using this hook the content container dose not receive focus.
  const { dialogProps } = useDialog({}, forwardRef);

  // Handle interacting outside the dialog and pressing
  // the Escape key to close the modal.
  const { overlayProps } = useOverlay(
    {
      onClose,
      isOpen,
      isDismissable: true,
    },
    forwardRef,
  );

  return (
    <FocusScope restoreFocus>
      <motion.div
        {...mergeProps(
          overlayProps,
          dialogProps,
          defaultOverlayProps,
          positionProps,
          modalProps,
        )}
        ref={(node) => {
          // eslint-disable-next-line no-param-reassign
          forwardRef.current = node;
          setPopperElement(node);
        }}
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          transition: { duration: 0.2, ease: "easeOut" },
        }}
        exit={{
          opacity: 0,
          transition: { duration: 0.15, ease: "easeIn" },
        }}
        className={className}
      >
        {children}
        <DismissButton onDismiss={onClose} />
      </motion.div>
    </FocusScope>
  );
});
export function Content({ children, className }) {
  const { isOpen, onClose, overlayRef } = React.useContext(PopoverContext);
  return (
    <AnimatePresence>
      {isOpen && (
        <OverlayContainer>
          <ContentBody
            className={className}
            ref={overlayRef}
            isOpen={isOpen}
            onClose={onClose}
          >
            {children}
          </ContentBody>
        </OverlayContainer>
      )}
    </AnimatePresence>
  );
}

export function Trigger({ children, ariaLabel, className }) {
  const { triggerProps, triggerRef } = React.useContext(PopoverContext);
  const { buttonProps } = useButton(
    { ...triggerProps, elementType: "div", "aria-label": ariaLabel },
    triggerRef,
  );

  return (
    <div
      {...buttonProps}
      ref={(node) => {
        triggerRef.current = node;
      }}
      className={className}
    >
      {children}
    </div>
  );
}

export function Root({
  children,
  isOpen,
  placement,
  offset,
  crossOffset,
  shouldFlip,
  onOpen,
  onClose,
}) {
  const state = useOverlayTriggerState({
    isOpen,
    onOpenChange: (open) => (open ? onOpen() : onClose()),
  });
  const triggerRef = React.useRef();
  const overlayRef = React.useRef();
  const [, setPopperElement] = React.useState(null);

  // Get props for the trigger and overlay. This also handles
  // hiding the overlay when a parent element of the trigger scrolls
  // (which invalidates the popover positioning).
  const { triggerProps, overlayProps } = useOverlayTrigger(
    { type: "menu" },
    state,
    triggerRef,
  );

  // Without using this hook when a parent element of the trigger scrolls the hiding dose not work.
  const { overlayProps: positionProps } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef,
    placement,
    shouldFlip,
    containerPadding: 0,
    isOpen: state.isOpen,
    offset,
    crossOffset,
  });

  return (
    <PopoverContext.Provider
      value={{
        overlayRef,
        triggerRef,
        overlayProps,
        triggerProps,
        isOpen: state.isOpen,
        onClose: state.close,
        setPopperElement,
        positionProps,
      }}
    >
      {children}
    </PopoverContext.Provider>
  );
}

Root.propTypes = {
  isOpen: PropTypes.bool,
  placement: PropTypes.oneOf(["bottom", "bottom start", "bottom end"]),
  offset: PropTypes.number,
  crossOffset: PropTypes.number,
  shouldFlip: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

Trigger.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

ContentBody.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  className: PropTypes.string,
};
Content.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  className: PropTypes.string,
};

Content.defaultProps = {
  className: undefined,
};

ContentBody.defaultProps = {
  className: undefined,
};
Trigger.defaultProps = {
  className: undefined,
  ariaLabel: undefined,
};

Root.defaultProps = {
  isOpen: false,
  shouldFlip: true,
  placement: "bottom",
  offset: 0,
  crossOffset: 0,
  onOpen: () => {},
  onClose: () => {},
};
